import React from 'react';
import { FamilyGroup } from '../../types';

interface GuestAssignment {
  is_adult: boolean;
  age?: number;
}

interface RoomConfig {
  room_type?: string;
  room_id?: number;
  quantity: number;
  foc?: number;
  extra_beds: number;
  kids_bf: number;
  kids_foc?: number;
  family_id?: number;
  guests_in_room?: GuestAssignment[];
  extra_bed_guests?: GuestAssignment[];
  kids_bf_guests?: GuestAssignment[];
  kids_foc_guests?: GuestAssignment[];
  // Quick count mode
  use_quick_count?: boolean;
  quick_adults_count?: number;
  quick_kids_count?: number;
}

interface Props {
  configs: RoomConfig[];
  onChange: (configs: RoomConfig[]) => void;
  availableRooms: Record<string, number>;
  hotelName?: string;
  families?: FamilyGroup[];
  onValidationChange?: (isValid: boolean) => void;
}

const RoomConfigBuilder: React.FC<Props> = ({ configs, onChange, availableRooms, hotelName, families, onValidationChange }) => {
  const addConfig = () => {
    onChange([...configs, { quantity: 1, foc: 0, extra_beds: 0, kids_bf: 0, kids_foc: 0 }]);
  };

  const removeConfig = (index: number) => {
    onChange(configs.filter((_, i) => i !== index));
  };

  const updateConfig = (index: number, updates: Partial<RoomConfig>) => {
    const updated = [...configs];
    updated[index] = { ...updated[index], ...updates };
    
    // Auto-clear guest assignments when counts are set to 0
    if ('quantity' in updates && updates.quantity === 0) {
      updated[index].guests_in_room = [];
    }
    if ('extra_beds' in updates && updates.extra_beds === 0) {
      updated[index].extra_bed_guests = [];
    }
    if ('kids_bf' in updates && updates.kids_bf === 0) {
      updated[index].kids_bf_guests = [];
    }
    if ('kids_foc' in updates && updates.kids_foc === 0) {
      updated[index].kids_foc_guests = [];
    }
    
    onChange(updated);
  };

  const hasRoomTypes = Object.keys(availableRooms).length > 0;

  const getSelectedFamily = (familyId?: number) => {
    return families?.find(f => f.family_id === familyId);
  };

  const getAvailableGuests = (config: RoomConfig) => {
    const family = getSelectedFamily(config.family_id);
    if (!family) return [];

    const guests: GuestAssignment[] = [];
    // Add adults
    for (let i = 0; i < family.no_of_pax; i++) {
      guests.push({ is_adult: true });
    }
    // Add kids with ages
    family.kids_ages?.forEach(age => {
      guests.push({ is_adult: false, age });
    });
    return guests;
  };

  const hasUnassignedGuests = () => {
    return configs.some(config => {
      if (config.family_id && getSelectedFamily(config.family_id)) {
        return getUnassignedGuests(config).length > 0;
      }
      return false;
    });
  };

  // Notify parent about validation state
  React.useEffect(() => {
    const isValid = !hasUnassignedGuests();
    onValidationChange?.(isValid);
  }, [configs, families, onValidationChange]);

  const getAssignedGuestsForFamily = (familyId?: number) => {
    const assigned: GuestAssignment[] = [];
    configs.forEach(config => {
      if (config.family_id === familyId) {
        if (config.guests_in_room) assigned.push(...config.guests_in_room);
        if (config.extra_bed_guests) assigned.push(...config.extra_bed_guests);
        if (config.kids_bf_guests) assigned.push(...config.kids_bf_guests);
        if (config.kids_foc_guests) assigned.push(...config.kids_foc_guests);
      }
    });
    return assigned;
  };

  const getUnassignedGuests = (config: RoomConfig) => {
    const available = getAvailableGuests(config);
    const assigned = getAssignedGuestsForFamily(config.family_id);
    
    const remaining = [...available];
    assigned.forEach(assignedGuest => {
      const idx = remaining.findIndex(g => 
        g.is_adult === assignedGuest.is_adult && g.age === assignedGuest.age
      );
      if (idx !== -1) remaining.splice(idx, 1);
    });
    return remaining;
  };

  const getUnassignedCount = (config: RoomConfig) => {
    const unassigned = getUnassignedGuests(config);
    const adults = unassigned.filter(g => g.is_adult).length;
    const kids = unassigned.filter(g => !g.is_adult).length;
    return { adults, kids };
  };

  const getQuickAssignedCount = (familyId?: number) => {
    let adults = 0;
    let kids = 0;
    configs.forEach(config => {
      if (config.family_id === familyId && config.use_quick_count) {
        adults += (config.quick_adults_count || 0) * config.quantity;
        kids += (config.quick_kids_count || 0) * config.quantity;
      }
    });
    return { adults, kids };
  };

  const formatGuest = (guest: GuestAssignment, index: number) => {
    if (guest.is_adult) return `Adult ${index + 1}`;
    return `Child (${guest.age}y)`;
  };

  const addGuestToRoom = (index: number, guest: GuestAssignment) => {
    const updated = [...configs];
    const current = updated[index].guests_in_room || [];
    updated[index] = { ...updated[index], guests_in_room: [...current, guest] };
    onChange(updated);
  };

  const removeGuestFromRoom = (index: number, guestIndex: number) => {
    const updated = [...configs];
    const current = updated[index].guests_in_room || [];
    updated[index] = { ...updated[index], guests_in_room: current.filter((_, i) => i !== guestIndex) };
    onChange(updated);
  };

  const addExtraBedGuest = (index: number, guest: GuestAssignment) => {
    const updated = [...configs];
    const current = updated[index].extra_bed_guests || [];
    updated[index] = { ...updated[index], extra_bed_guests: [...current, guest] };
    onChange(updated);
  };

  const removeExtraBedGuest = (index: number, guestIndex: number) => {
    const updated = [...configs];
    const current = updated[index].extra_bed_guests || [];
    updated[index] = { ...updated[index], extra_bed_guests: current.filter((_, i) => i !== guestIndex) };
    onChange(updated);
  };

  const addKidsBfGuest = (index: number, guest: GuestAssignment) => {
    if (guest.is_adult) return;
    const updated = [...configs];
    const current = updated[index].kids_bf_guests || [];
    updated[index] = { ...updated[index], kids_bf_guests: [...current, guest] };
    onChange(updated);
  };

  const removeKidsBfGuest = (index: number, guestIndex: number) => {
    const updated = [...configs];
    const current = updated[index].kids_bf_guests || [];
    updated[index] = { ...updated[index], kids_bf_guests: current.filter((_, i) => i !== guestIndex) };
    onChange(updated);
  };

  const addKidsFocGuest = (index: number, guest: GuestAssignment) => {
    if (guest.is_adult) return;
    const updated = [...configs];
    const current = updated[index].kids_foc_guests || [];
    updated[index] = { ...updated[index], kids_foc_guests: [...current, guest] };
    onChange(updated);
  };

  const removeKidsFocGuest = (index: number, guestIndex: number) => {
    const updated = [...configs];
    const current = updated[index].kids_foc_guests || [];
    updated[index] = { ...updated[index], kids_foc_guests: current.filter((_, i) => i !== guestIndex) };
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <label className="text-sm font-medium text-gray-700">Room Configurations</label>
          {hotelName && <p className="text-xs text-gray-500">Configure multiple room types for {hotelName}</p>}
        </div>
        <button
          type="button"
          onClick={addConfig}
          className="text-xs bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
        >
          + Add Room Type
        </button>
      </div>

      {!hasRoomTypes && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
          ⚠️ Please select a hotel first to see available room types
        </div>
      )}

      {/* Guest Assignment Warning - Nyepi style */}
      {hasUnassignedGuests() && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Guest Assignment Required
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  Some guests are not assigned to rooms. This may be due to recent changes in family information (age updates). 
                  Please review and assign all guests to appropriate rooms before proceeding.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {configs.map((config, index) => (
        <div key={index} className="border border-gray-300 rounded-lg p-3 bg-white">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-gray-600">
              {config.room_type || `Room Config ${index + 1}`}
            </span>
            {configs.length > 1 && (
              <button
                type="button"
                onClick={() => removeConfig(index)}
                className="text-xs text-red-600 hover:text-red-800"
              >
                ✕ Remove
              </button>
            )}
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                {families && families.length > 0 ? 'Family *' : 'Family ID'}
              </label>
              {families && families.length > 0 ? (
                <select
                  value={config.family_id || ''}
                  onChange={(e) => updateConfig(index, { family_id: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="input-field text-xs w-full"
                >
                  <option value="">Select Family</option>
                  {families.map(f => (
                    <option key={f.family_id} value={f.family_id}>
                      Family {f.family_id} ({f.no_of_pax} adults, {f.no_of_kids} kids{f.kids_ages?.length > 0 ? ` - ${f.kids_ages.join(', ')}y` : ''})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="number"
                  min="1"
                  value={config.family_id || ''}
                  onChange={(e) => updateConfig(index, { family_id: e.target.value ? parseInt(e.target.value) : undefined })}
                  placeholder="1, 2, 3..."
                  className="input-field text-xs w-full"
                />
              )}
            </div>
            
            {hasRoomTypes && (
              <div>
                <label className="block text-xs text-gray-600 mb-1">Room Type *</label>
                <select
                  value={config.room_id || ''}
                  onChange={(e) => {
                    const roomId = parseInt(e.target.value);
                    const roomType = Object.entries(availableRooms).find(([_, id]) => id === roomId)?.[0];
                    updateConfig(index, { room_id: roomId, room_type: roomType });
                  }}
                  className="input-field text-xs w-full"
                  required
                >
                  <option value="">Select Room Type</option>
                  {Object.entries(availableRooms).map(([type, id]) => (
                    <option key={id} value={id}>{type}</option>
                  ))}
                </select>
              </div>
            )}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Rooms *</label>
              <input
                type="number"
                min="1"
                value={config.quantity}
                onChange={(e) => updateConfig(index, { quantity: parseInt(e.target.value) || 1 })}
                className="input-field text-xs w-full"
                required
              />
              {/* Toggle between modes */}
              {config.family_id && getSelectedFamily(config.family_id) && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => updateConfig(index, { use_quick_count: !config.use_quick_count })}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    {config.use_quick_count ? '→ Detailed Assignment' : '→ Quick Count'}
                  </button>
                </div>
              )}
              {/* Quick Count Mode */}
              {config.family_id && getSelectedFamily(config.family_id) && config.use_quick_count && (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-gray-600">
                    Unassigned: {(() => {
                      const unassigned = getUnassignedCount(config);
                      return `${unassigned.adults}A, ${unassigned.kids}K`;
                    })()}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500">Adults/room</label>
                      <input
                        type="number"
                        min="0"
                        value={Math.floor((config.guests_in_room || []).filter(g => g.is_adult).length / config.quantity)}
                        onChange={(e) => {
                          const count = parseInt(e.target.value) || 0;
                          const unassigned = getUnassignedGuests(config);
                          const adultsNeeded = count * config.quantity;
                          const currentAdults = (config.guests_in_room || []).filter(g => g.is_adult);
                          const currentKids = (config.guests_in_room || []).filter(g => !g.is_adult);
                          
                          if (adultsNeeded > currentAdults.length) {
                            // Add more adults
                            const availableAdults = unassigned.filter(g => g.is_adult);
                            const toAdd = availableAdults.slice(0, adultsNeeded - currentAdults.length);
                            updateConfig(index, { guests_in_room: [...currentAdults, ...toAdd, ...currentKids] });
                          } else {
                            // Remove adults
                            updateConfig(index, { guests_in_room: [...currentAdults.slice(0, adultsNeeded), ...currentKids] });
                          }
                        }}
                        className="input-field text-xs w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">Kids/room</label>
                      <input
                        type="number"
                        min="0"
                        value={Math.floor((config.guests_in_room || []).filter(g => !g.is_adult).length / config.quantity)}
                        onChange={(e) => {
                          const count = parseInt(e.target.value) || 0;
                          const unassigned = getUnassignedGuests(config);
                          const kidsNeeded = count * config.quantity;
                          const currentAdults = (config.guests_in_room || []).filter(g => g.is_adult);
                          const currentKids = (config.guests_in_room || []).filter(g => !g.is_adult);
                          
                          if (kidsNeeded > currentKids.length) {
                            // Add more kids
                            const availableKids = unassigned.filter(g => !g.is_adult);
                            const toAdd = availableKids.slice(0, kidsNeeded - currentKids.length);
                            updateConfig(index, { guests_in_room: [...currentAdults, ...currentKids, ...toAdd] });
                          } else {
                            // Remove kids
                            updateConfig(index, { guests_in_room: [...currentAdults, ...currentKids.slice(0, kidsNeeded)] });
                          }
                        }}
                        className="input-field text-xs w-full"
                      />
                    </div>
                  </div>
                  <div className="text-xs text-blue-600">
                    Assigned: {(config.guests_in_room || []).filter(g => g.is_adult).length}A, {(config.guests_in_room || []).filter(g => !g.is_adult).length}K
                  </div>
                  {/* Show assigned guests */}
                  {(config.guests_in_room || []).length > 0 && (
                    <div className="mt-2">
                      <label className="block text-xs text-gray-600 mb-1">Guest Assignment:</label>
                      <div className="flex flex-wrap gap-1">
                        {(config.guests_in_room || []).map((guest, gIdx) => (
                          <span key={gIdx} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                            {formatGuest(guest, config.guests_in_room?.filter(g => g.is_adult === guest.is_adult).indexOf(guest) || 0)}
                            <button
                              type="button"
                              onClick={() => removeGuestFromRoom(index, gIdx)}
                              className="text-blue-600 hover:text-blue-800 font-bold"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Detailed Assignment Mode */}
              {config.family_id && getSelectedFamily(config.family_id) && !config.use_quick_count && (
                <div className="mt-2">
                  <label className="block text-xs text-gray-600 mb-1">Guest Assignment:</label>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {(config.guests_in_room || []).map((guest, gIdx) => (
                      <span key={gIdx} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        {formatGuest(guest, config.guests_in_room?.filter(g => g.is_adult === guest.is_adult).indexOf(guest) || 0)}
                        <button
                          type="button"
                          onClick={() => removeGuestFromRoom(index, gIdx)}
                          className="text-blue-600 hover:text-blue-800 font-bold"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  {getUnassignedGuests(config).length > 0 && (
                    <select
                      onChange={(e) => {
                        const guestData = JSON.parse(e.target.value);
                        addGuestToRoom(index, guestData);
                        e.target.value = '';
                      }}
                      className="input-field text-xs w-full"
                      value=""
                    >
                      <option value="">+ Add guest</option>
                      {getUnassignedGuests(config).map((guest, gIdx) => {
                        const adultCount = getUnassignedGuests(config).filter((g, i) => i <= gIdx && g.is_adult).length;
                        const childCount = getUnassignedGuests(config).filter((g, i) => i <= gIdx && !g.is_adult && g.age === guest.age).length;
                        return (
                          <option key={gIdx} value={JSON.stringify(guest)}>
                            {formatGuest(guest, guest.is_adult ? adultCount - 1 : childCount - 1)}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              )}
            </div>

            <div>
              <div className="relative group">
                <label className="block text-xs text-gray-600 mb-1 cursor-help">FOC Rooms</label>
                <div className="absolute bottom-full left-0 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  Complimentary rooms
                </div>
              </div>
              <input
                type="number"
                min="0"
                value={config.foc || 0}
                onChange={(e) => updateConfig(index, { foc: parseInt(e.target.value) || 0 })}
                className="input-field text-xs w-full"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Kids (FOC)</label>
              <input
                type="number"
                min="0"
                value={config.kids_foc || 0}
                onChange={(e) => updateConfig(index, { kids_foc: parseInt(e.target.value) || 0 })}
                className="input-field text-xs w-full"
              />
              {/* Kids FOC Guest Assignment */}
              {config.family_id && getSelectedFamily(config.family_id) && (config.kids_foc || 0) > 0 && (
                <div className="mt-2">
                  <label className="block text-xs text-gray-600 mb-1">Guest Assignment:</label>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {(config.kids_foc_guests || []).map((guest, gIdx) => (
                      <span key={gIdx} className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                        {formatGuest(guest, config.kids_foc_guests?.filter(g => g.is_adult === guest.is_adult).indexOf(guest) || 0)}
                        <button
                          type="button"
                          onClick={() => removeKidsFocGuest(index, gIdx)}
                          className="text-purple-600 hover:text-purple-800 font-bold"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  {getUnassignedGuests(config).filter(g => !g.is_adult).length > 0 && (config.kids_foc_guests?.length || 0) < (config.kids_foc || 0) && (
                    <select
                      onChange={(e) => {
                        const guestData = JSON.parse(e.target.value);
                        addKidsFocGuest(index, guestData);
                        e.target.value = '';
                      }}
                      className="input-field text-xs w-full"
                      value=""
                    >
                      <option value="">+ Add child</option>
                      {getUnassignedGuests(config).filter(g => !g.is_adult).map((guest, gIdx) => {
                        const childCount = getUnassignedGuests(config).filter((g, i) => i <= gIdx && !g.is_adult && g.age === guest.age).length;
                        return (
                          <option key={gIdx} value={JSON.stringify(guest)}>
                            {formatGuest(guest, childCount - 1)}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Kids BF</label>
              <input
                type="number"
                min="0"
                value={config.kids_bf}
                onChange={(e) => updateConfig(index, { kids_bf: parseInt(e.target.value) || 0 })}
                className="input-field text-xs w-full"
              />
              {/* Kids Breakfast Guest Assignment */}
              {config.family_id && getSelectedFamily(config.family_id) && config.kids_bf > 0 && (
                <div className="mt-2">
                  <label className="block text-xs text-gray-600 mb-1">Guest Assignment:</label>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {(config.kids_bf_guests || []).map((guest, gIdx) => (
                      <span key={gIdx} className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                        {formatGuest(guest, config.kids_bf_guests?.filter(g => g.is_adult === guest.is_adult).indexOf(guest) || 0)}
                        <button
                          type="button"
                          onClick={() => removeKidsBfGuest(index, gIdx)}
                          className="text-yellow-600 hover:text-yellow-800 font-bold"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  {getUnassignedGuests(config).filter(g => !g.is_adult).length > 0 && (config.kids_bf_guests?.length || 0) < config.kids_bf && (
                    <select
                      onChange={(e) => {
                        const guestData = JSON.parse(e.target.value);
                        addKidsBfGuest(index, guestData);
                        e.target.value = '';
                      }}
                      className="input-field text-xs w-full"
                      value=""
                    >
                      <option value="">+ Add child</option>
                      {getUnassignedGuests(config).filter(g => !g.is_adult).map((guest, gIdx) => {
                        const childCount = getUnassignedGuests(config).filter((g, i) => i <= gIdx && !g.is_adult && g.age === guest.age).length;
                        return (
                          <option key={gIdx} value={JSON.stringify(guest)}>
                            {formatGuest(guest, childCount - 1)}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Extra Beds</label>
              <input
                type="number"
                min="0"
                value={config.extra_beds}
                onChange={(e) => updateConfig(index, { extra_beds: parseInt(e.target.value) || 0 })}
                className="input-field text-xs w-full"
              />
              {/* Extra Bed Guest Assignment */}
              {config.family_id && getSelectedFamily(config.family_id) && config.extra_beds > 0 && (
                <div className="mt-2">
                  <label className="block text-xs text-gray-600 mb-1">Guest Assignment:</label>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {(config.extra_bed_guests || []).map((guest, gIdx) => (
                      <span key={gIdx} className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                        {formatGuest(guest, config.extra_bed_guests?.filter(g => g.is_adult === guest.is_adult).indexOf(guest) || 0)}
                        <button
                          type="button"
                          onClick={() => removeExtraBedGuest(index, gIdx)}
                          className="text-green-600 hover:text-green-800 font-bold"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  {getUnassignedGuests(config).length > 0 && (config.extra_bed_guests?.length || 0) < config.extra_beds && (
                    <select
                      onChange={(e) => {
                        const guestData = JSON.parse(e.target.value);
                        addExtraBedGuest(index, guestData);
                        e.target.value = '';
                      }}
                      className="input-field text-xs w-full"
                      value=""
                    >
                      <option value="">+ Add guest</option>
                      {getUnassignedGuests(config).map((guest, gIdx) => {
                        const adultCount = getUnassignedGuests(config).filter((g, i) => i <= gIdx && g.is_adult).length;
                        const childCount = getUnassignedGuests(config).filter((g, i) => i <= gIdx && !g.is_adult && g.age === guest.age).length;
                        return (
                          <option key={gIdx} value={JSON.stringify(guest)}>
                            {formatGuest(guest, guest.is_adult ? adultCount - 1 : childCount - 1)}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              )}
            </div>
            </div>

            {/* Unassigned Warning */}
            {config.family_id && getSelectedFamily(config.family_id) && !config.use_quick_count && getUnassignedGuests(config).length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800 mt-2">
                ⚠️ {getUnassignedGuests(config).length} guest(s) not assigned yet
              </div>
            )}
          </div>
        </div>
      ))}

      {configs.length === 0 && (
        <div className="text-center py-4 text-xs text-gray-500 border border-dashed border-gray-300 rounded">
          No configurations. Click "+ Add Room Type" to start.
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
        <strong>Summary:</strong> {configs.reduce((sum, c) => sum + c.quantity, 0)} rooms
        {configs.reduce((sum, c) => sum + (c.foc || 0), 0) > 0 && (
          <span className="text-orange-600"> + {configs.reduce((sum, c) => sum + (c.foc || 0), 0)} FOC</span>
        )}
        {', '}{configs.reduce((sum, c) => sum + c.extra_beds, 0)} extra beds
        {configs.some(c => c.family_id) && (
          <span className="ml-2">• {new Set(configs.map(c => c.family_id).filter(Boolean)).size} families</span>
        )}
      </div>
      
      {/* Family Assignment Warning */}
      {families && families.length > 0 && (
        (() => {
          const assignedFamilies = new Set(configs.map(c => c.family_id).filter(Boolean));
          const unassignedFamilies = families.filter(f => !assignedFamilies.has(f.family_id));
          return unassignedFamilies.length > 0 ? (
            <div className="bg-yellow-50 border border-yellow-400 rounded p-2 text-xs text-yellow-800">
              ⚠️ <strong>Warning:</strong> {unassignedFamilies.length} family/families not assigned to any room:
              {unassignedFamilies.map(f => (
                <span key={f.family_id} className="ml-2">
                  Family {f.family_id} ({f.no_of_pax}A + {f.no_of_kids}C)
                </span>
              ))}
            </div>
          ) : null;
        })()
      )}
    </div>
  );
};

// Export validation function for parent components
export const validateRoomAssignments = (configs: RoomConfig[], families?: FamilyGroup[]): boolean => {
  const getSelectedFamily = (familyId?: number) => {
    return families?.find(f => f.family_id === familyId);
  };

  const getAvailableGuests = (config: RoomConfig) => {
    const family = getSelectedFamily(config.family_id);
    if (!family) return [];

    const guests: { is_adult: boolean; age?: number }[] = [];
    for (let i = 0; i < family.no_of_pax; i++) {
      guests.push({ is_adult: true });
    }
    family.kids_ages?.forEach(age => {
      guests.push({ is_adult: false, age });
    });
    return guests;
  };

  const getAssignedGuestsForFamily = (familyId?: number) => {
    const assigned: { is_adult: boolean; age?: number }[] = [];
    configs.forEach(config => {
      if (config.family_id === familyId) {
        if (config.guests_in_room) assigned.push(...config.guests_in_room);
        if (config.extra_bed_guests) assigned.push(...config.extra_bed_guests);
        if (config.kids_bf_guests) assigned.push(...config.kids_bf_guests);
        if (config.kids_foc_guests) assigned.push(...config.kids_foc_guests);
      }
    });
    return assigned;
  };

  const getUnassignedGuests = (config: RoomConfig) => {
    const available = getAvailableGuests(config);
    const assigned = getAssignedGuestsForFamily(config.family_id);
    
    const remaining = [...available];
    assigned.forEach(assignedGuest => {
      const idx = remaining.findIndex(g => 
        g.is_adult === assignedGuest.is_adult && g.age === assignedGuest.age
      );
      if (idx !== -1) remaining.splice(idx, 1);
    });
    return remaining;
  };

  return !configs.some(config => {
    if (config.family_id && getSelectedFamily(config.family_id)) {
      return getUnassignedGuests(config).length > 0;
    }
    return false;
  });
};

export default RoomConfigBuilder;
